/**
 * Input-token budgeting for Azure OpenAI chat completions.
 *
 * The chat request sent to Azure is `[{system: prompt + retrieval context},
 * ...full conversation history]`. Nothing in the request path bounds that
 * payload, so a long conversation or a large retrieval context can exceed the
 * deployment's context window and trigger a `400 context_length_exceeded`
 * (the output cap `max_completion_tokens` does NOT constrain input).
 *
 * This module estimates token counts cheaply (no tokenizer dependency) and
 * trims the request to fit a configured window before it is sent:
 *   - the retrieval context is capped to a reserved slice of the budget, and
 *   - the conversation history is trimmed with a sliding window that always
 *     keeps the newest turn.
 *
 * Estimation is intentionally a coarse `chars / CHARS_PER_TOKEN` heuristic.
 * It runs against a budget that already subtracts a safety margin, so small
 * per-language inaccuracies stay well inside the slack.
 */
import type { ContentPart, OpenAIMessage } from '@/types';
import { API_CONFIG, MESSAGE_LIMITS } from './constants';

/** Average characters per token for the estimation heuristic. */
export const CHARS_PER_TOKEN = 4;
/**
 * Flat per-image token charge. Azure tiles images rather than counting the
 * base64 string as text; this is a conservative high-detail upper bound so we
 * never under-count a vision turn.
 */
export const IMAGE_TOKEN_ESTIMATE = 1200;
/** Per-message overhead (role + delimiters) the API adds around content. */
export const MESSAGE_TOKEN_OVERHEAD = 4;
/** Absolute floor for the buffer kept free below the model window. */
export const TOKEN_SAFETY_MARGIN = 2000;
/**
 * Fraction of the window reserved as additional headroom. The `chars/4`
 * estimate under-counts dense content (CSV, numbers, code) — on a large
 * window a flat 2k buffer is far too small to absorb that error, so the
 * effective margin scales with the window: `max(2000, inputLimit * 0.12)`.
 */
export const SAFETY_MARGIN_FRACTION = 0.12;
/**
 * Characters of extracted file content allowed per token of the agent's
 * context window. Deliberately conservative: dense content (CSV/numbers) runs
 * ~2 chars/token, so 1.5 chars/window-token caps a file at ≲75% of the window
 * even in the worst case, leaving room for the prompt, history, and output —
 * and the token budgeter is still the final gate. Scales the per-file cap to
 * the model: a 922k window admits ~1.38M chars, a 120k window ~180k.
 */
export const FILE_CONTENT_CHARS_PER_WINDOW_TOKEN = 1.5;

/**
 * Per-file character cap for extracted text (ALL types — XLSX, DOCX, PDF,
 * TXT), scaled to the agent's context window so a big-window model can ingest
 * a larger spreadsheet/document while a small window stays bounded. Floored at
 * MESSAGE_LIMITS.MAX_CONTEXT_LENGTH so an unset/tiny window still accepts a
 * useful amount. An undefined/zero window falls back to the conservative
 * default window.
 */
export function fileContentCharBudget(inputLimitTokens?: number): number {
  const limit =
    inputLimitTokens && inputLimitTokens > 0
      ? inputLimitTokens
      : API_CONFIG.DEFAULT_CONTEXT_WINDOW_TOKENS;
  const scaled = Math.floor(limit * FILE_CONTENT_CHARS_PER_WINDOW_TOKEN);
  return Math.max(MESSAGE_LIMITS.MAX_CONTEXT_LENGTH, scaled);
}
/** Tokens reserved for the newest turn before the context may consume budget. */
export const MIN_HISTORY_RESERVE_TOKENS = 1000;

const TRUNCATION_NOTE =
  '\n\n[Context truncated to fit the model input limit.]';

// Exact tokenizer (o200k_base — gpt-4o / gpt-4.1 / gpt-5 family). Lazily
// loaded so the sizeable rank table stays out of the initial bundle; until
// it's ready (and in test mode) estimateTokens falls back to the chars/4
// heuristic. The heuristic under-counts dense content (CSV/numbers/code) by
// up to ~2x, which is what lets a large spreadsheet slip past the budget — so
// production loads the real tokenizer to count exactly.
let tokenizerEncode: ((text: string) => number) | null = null;
let tokenizerLoad: Promise<void> | null = null;

/**
 * Ensure the exact tokenizer is loaded before budgeting. Memoized. A no-op in
 * test mode (keeps unit tests deterministic on the chars/4 fallback) and on
 * any load failure (graceful degrade to the heuristic). Callers should
 * `await` this before applyTokenBudget so counts are exact.
 */
export function ensureTokenizer(): Promise<void> {
  if (tokenizerLoad) return tokenizerLoad;
  if (tokenizerEncode || import.meta.env.MODE === 'test') {
    tokenizerLoad = Promise.resolve();
    return tokenizerLoad;
  }
  tokenizerLoad = import('gpt-tokenizer/encoding/o200k_base')
    .then((mod) => {
      tokenizerEncode = (text: string) => mod.encode(text).length;
    })
    .catch((err: unknown) => {
      console.warn(
        '[tokenBudget] tokenizer load failed; using chars/4 heuristic',
        err
      );
    });
  return tokenizerLoad;
}

/** Test-only: directly set/reset the tokenizer fn (bypasses lazy loading). */
export function _setTokenizerForTesting(
  fn: ((text: string) => number) | null
): void {
  tokenizerEncode = fn;
  tokenizerLoad = null;
}

/** Estimate the token count of a plain string (exact when the tokenizer is loaded). */
export function estimateTokens(text: string): number {
  if (!text) return 0;
  if (tokenizerEncode) {
    try {
      return tokenizerEncode(text);
    } catch {
      // Fall through to the heuristic on any tokenizer error.
    }
  }
  return Math.ceil(text.length / CHARS_PER_TOKEN);
}

/** Estimate tokens for string or multimodal (text + image) message content. */
export function estimateContentTokens(
  content: OpenAIMessage['content']
): number {
  if (typeof content === 'string') {
    return estimateTokens(content);
  }
  return content.reduce((sum: number, part: ContentPart) => {
    if (part.type === 'text') {
      return sum + estimateTokens(part.text);
    }
    return sum + IMAGE_TOKEN_ESTIMATE;
  }, 0);
}

/** Estimate tokens for a full message, including per-message overhead. */
export function estimateMessageTokens(msg: OpenAIMessage): number {
  return estimateContentTokens(msg.content) + MESSAGE_TOKEN_OVERHEAD;
}

/**
 * Cap `text` so its estimate fits within `maxTokens`. Truncated text gets a
 * short trailing note so the model (and reader) knows content was dropped.
 * A non-positive budget yields empty text.
 */
export function capTextToTokens(
  text: string,
  maxTokens: number
): { text: string; truncated: boolean } {
  if (estimateTokens(text) <= maxTokens && maxTokens > 0) {
    return { text, truncated: false };
  }
  if (maxTokens <= 0) {
    return { text: '', truncated: true };
  }
  const noteTokens = estimateTokens(TRUNCATION_NOTE);
  const keepChars = Math.max(0, (maxTokens - noteTokens) * CHARS_PER_TOKEN);
  return { text: text.slice(0, keepChars) + TRUNCATION_NOTE, truncated: true };
}

/**
 * Trim conversation history to `budgetTokens` with a sliding window: keep the
 * newest messages and drop the oldest. The most recent message is always kept
 * (even if it alone exceeds the budget) so the request is never empty — the
 * caller decides what to do when that single message still overflows.
 * Returns the kept messages in their original chronological order.
 */
export function trimHistoryToBudget(
  messages: OpenAIMessage[],
  budgetTokens: number
): { messages: OpenAIMessage[]; droppedCount: number } {
  if (messages.length === 0) {
    return { messages: [], droppedCount: 0 };
  }

  const kept: OpenAIMessage[] = [];
  let used = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const cost = estimateMessageTokens(messages[i]);
    if (kept.length === 0) {
      // Always keep the newest message, regardless of budget.
      kept.push(messages[i]);
      used += cost;
      continue;
    }
    if (used + cost > budgetTokens) {
      break;
    }
    kept.push(messages[i]);
    used += cost;
  }

  kept.reverse();
  return { messages: kept, droppedCount: messages.length - kept.length };
}

export interface BudgetInput {
  /** System prompt WITHOUT the retrieval context (prompt + citation rules). */
  basePrompt: string;
  /** Retrieval / file / web context appended to the system prompt. */
  context: string;
  /** Conversation history including the current user turn. */
  messages: OpenAIMessage[];
  /** The deployment's context window in tokens. */
  inputLimit: number;
  /** Tokens reserved for the model's output (`max_completion_tokens`). */
  outputReserve: number;
}
export interface BudgetResult {
  /** Context after capping (possibly truncated or emptied). */
  context: string;
  /** History after trimming. */
  messages: OpenAIMessage[];
  contextTruncated: boolean;
  droppedMessages: number;
  /**
   * True when, even after capping context and trimming to the newest turn,
   * the estimated payload still exceeds the model window. The caller should
   * surface an actionable error instead of sending a doomed request.
   */
  overflow: boolean;
}

/**
 * Fit a chat request to the model window: cap the retrieval context to a
 * reserved slice of the available budget, then trim history to whatever
 * remains, always keeping the newest turn.
 */
export function applyTokenBudget(input: BudgetInput): BudgetResult {
  const { basePrompt, messages, inputLimit, outputReserve } = input;

  const margin = Math.max(
    TOKEN_SAFETY_MARGIN,
    Math.ceil(inputLimit * SAFETY_MARGIN_FRACTION)
  );
  const available = inputLimit - outputReserve - margin;
  const baseTokens = estimateTokens(basePrompt);

  // 1) Cap the retrieval context, reserving room for at least the newest turn.
  let context = input.context;
  let contextTruncated = false;
  if (context) {
    const contextBudget = available - baseTokens - MIN_HISTORY_RESERVE_TOKENS;
    if (contextBudget <= 0) {
      // No room for any context once the base prompt + reserve are accounted
      // for. Drop it entirely rather than send a doomed payload.
      context = '';
      contextTruncated = true;
    } else if (estimateTokens(context) > contextBudget) {
      const capped = capTextToTokens(context, contextBudget);
      context = capped.text;
      contextTruncated = capped.truncated;
    }
  }

  // 2) Trim history to whatever the system prompt leaves behind.
  const systemTokens = baseTokens + estimateTokens(context);
  const historyBudget = available - systemTokens;
  const trimmed = trimHistoryToBudget(messages, historyBudget);

  // 3) Overflow check against the HARD limit (not the conservative budget):
  //    only declare a request unsendable when it truly cannot fit.
  const keptTokens = trimmed.messages.reduce(
    (sum, m) => sum + estimateMessageTokens(m),
    0
  );
  const overflow = systemTokens + keptTokens > inputLimit - outputReserve;

  return {
    context,
    messages: trimmed.messages,
    contextTruncated,
    droppedMessages: trimmed.droppedCount,
    overflow,
  };
}
